import React, { useMemo, useState } from "react";
import "../layout/crop/Popup/CropPrepControls.css";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const filterNumericInput = (raw, { allowNegative = false, maxLength = 4 } = {}) => {
  if (!allowNegative) {
    return raw.replace(/[^0-9]/g, "").slice(0, maxLength);
  }

  let next = raw.replace(/[^0-9-]/g, "");
  const negative = next.startsWith("-");
  next = next.replace(/-/g, "");
  next = (negative ? "-" : "") + next;
  return next.slice(0, maxLength + (negative ? 1 : 0));
};

/**
 * Circle Crop–style slider row: label + numeric input on top, range slider below.
 */
const ToolPrepSliderField = ({
  label,
  value,
  min,
  max,
  unit = "",
  disabled = false,
  allowNegative = false,
  maxInputLength = 4,
  formatValue = (v) => String(v),
  parseValue = (raw) => Number(raw),
  onChange,
  onCommit,
}) => {
  const [draft, setDraft] = useState(null);
  const displayValue = draft != null ? draft : formatValue(value);

  const fillPct = useMemo(() => {
    const span = max - min;
    if (!span) return 0;
    return clamp(((value - min) / span) * 100, 0, 100);
  }, [max, min, value]);

  const commitDraft = () => {
    const raw = draft != null ? draft : formatValue(value);
    const parsed = parseValue(raw === "" || raw === "-" ? min : raw);
    const next = clamp(parsed, min, max);
    onChange(next);
    onCommit?.();
    setDraft(null);
  };

  return (
    <div className="crop-prep-controls__section">
      <div className="crop-prep-controls__slider-head">
        <span className="crop-prep-controls__section-title">{label}</span>
        <div className="crop-prep-controls__slider-value">
          <input
            type="text"
            inputMode={allowNegative ? "text" : "numeric"}
            className="crop-prep-controls__slider-input"
            value={displayValue}
            disabled={disabled}
            aria-label={`${label} value`}
            onChange={(e) => {
              setDraft(
                filterNumericInput(e.target.value, {
                  allowNegative,
                  maxLength: maxInputLength,
                })
              );
            }}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitDraft();
                e.currentTarget.blur();
              }
            }}
          />
          {unit ? (
            <span className="crop-prep-controls__slider-suffix">{unit}</span>
          ) : null}
        </div>
      </div>
      <input
        type="range"
        className="crop-prep-controls__slider"
        min={min}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        aria-label={`${label} slider`}
        style={{ "--crop-slider-fill": `${fillPct}%` }}
        onChange={(e) => {
          setDraft(null);
          onChange(Number(e.target.value));
        }}
        onPointerDown={() => onCommit?.()}
      />
    </div>
  );
};

export default ToolPrepSliderField;
